/**
 * Dropdown Component
 * Reusable dropdown component with search and selection
 */

import React, { useState, useRef, useEffect } from 'react';
import styles from './Dropdown.module.css';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  searchable?: boolean;
  label?: string;
}

function Dropdown({
    options,
    value,
    placeholder = 'Select an option',
    onChange,
    className = '',
    disabled = false,
    searchable = false,
    label
}: DropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(option => option.value === value);

    const filteredOptions = searchable 
        ? options.filter(option => 
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : options;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option: DropdownOption) => {
        if (!option.disabled) {
            onChange(option.value);
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(!isOpen);
        } else if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div 
            ref={dropdownRef}
            className={`${styles.dropdown} ${className}`}
        >
            {label && (
                <label className={styles.label}>
                    {label}
                </label>
            )}
            <button
                type="button"
                className={`${styles.trigger} ${isOpen ? styles.trigger__open : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className={styles.triggerText}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <svg 
                    className={`${styles.chevron} ${isOpen ? styles.chevron__open : ''}`}
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                >
                    <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
            </button>

            {isOpen && (
                <div className={styles.menu}>
                    {searchable && (
                        <div className={styles.search}>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search options..."
                                className={styles.searchInput}
                                autoFocus
                            />
                        </div>
                    )}
          
                    <div className={styles.options} role="listbox">
                        {filteredOptions.length === 0 ? (
                            <div className={styles.noOptions}>
                                No options found
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`${styles.option} ${
                                        option.value === value ? styles.option__selected : ''
                                    } ${option.disabled ? styles.option__disabled : ''}`}
                                    onClick={() => handleSelect(option)}
                                    disabled={option.disabled}
                                    role="option"
                                    aria-selected={option.value === value}
                                >
                                    {option.label}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default React.memo(Dropdown);
